import React, { useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  useTheme,
  CircularProgress,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import TodayIcon from "@mui/icons-material/Today";
import { useGetListOfBugsQuery } from "@/features/bughurd/api";
import { TimelineHeader } from "./TimelineHeader";
import { TimelineChart } from "./TimelineChart";
import { useParams } from "react-router-dom";
import TaskUpdateDrawer from "@/pages/Board/bugherdComponents/TaskUpdateDrawer";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
interface TimelineViewProps {
  sprintId?: string;
}

interface TransformedTask {
  _id: string;
  title: string;
  taskKey: string;
  type: string;
  startDate: string | null;
  endDate: string | null;
  hasValidDates: boolean;
  hasDueDate: boolean;
  hasStartDate: boolean;
  assignees: Array<{
    _id: string;
    fullName: string;
    email: string;
  }>;
  hours?: number;
  progress?: number;
  color: string;
  taskCount?: number;
  children?: TransformedTask[];
}

type ViewMode = "today" | "weeks" | "months" | "quarters";

export const TimelineView: React.FC<TimelineViewProps> = ({ sprintId }) => {
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>("weeks");
  const { id: projectId } = useParams();
  const theme = useTheme();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Refs for scroll sync
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const taskNamesScrollRef = useRef<HTMLDivElement>(null);

  const {
    data: bugsDataQuery,
    isLoading: isBugsLoading,
    refetch: refetchBugs,
  } = useGetListOfBugsQuery(
    {
      id: projectId,
      forBoard: true,
      sprint: sprintId,
      viewType: "TIMELINE",
      showEpic: true,
      page: 1,
      limit: 100,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
    setIsDrawerOpen(true);
  };
  const { data: companyMemberDataQuery, isLoading: isMembersLoading } =
    useGetCompanyMembersQuery(1);
  const users_data = companyMemberDataQuery?.data?.decryptedData || [];
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedTaskId(null);
  };

  const handleTaskUpdate = () => {
    // refetchBugs();
  };

  const { startDate, endDate } = useMemo(() => {
    const today = new Date();
    let start: Date, end: Date;

    switch (viewMode) {
      case "today":
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        end = new Date(today);
        end.setDate(end.getDate() + 7);
        break;

      case "weeks":
        start = new Date(today);
        start.setDate(start.getDate() - 14);
        end = new Date(today);
        end.setDate(end.getDate() + 42);
        break;

      case "months":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 3, 0);
        break;

      case "quarters":
        const currentQuarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), currentQuarter * 3 - 3, 1);
        end = new Date(today.getFullYear(), currentQuarter * 3 + 6, 0);
        break;

      default:
        start = new Date(today);
        start.setDate(start.getDate() - 14);
        end = new Date(today);
        end.setDate(end.getDate() + 42);
    }

    return { startDate: start, endDate: end };
  }, [viewMode]);

  const parseEstimation = (estimation: string): number => {
    if (!estimation) return 0;

    let totalHours = 0;
    const dayMatch = estimation.match(/(\d+)d/);
    const hourMatch = estimation.match(/(\d+)h/);
    const minuteMatch = estimation.match(/(\d+)m/);

    if (dayMatch) totalHours += parseInt(dayMatch[1]) * 8;
    if (hourMatch) totalHours += parseInt(hourMatch[1]);
    if (minuteMatch) totalHours += parseInt(minuteMatch[1]) / 60;

    return Math.round(totalHours * 10) / 10;
  };

  const calculateProgress = (task: any): number => {
    const statusCode = task.bugStatus?.statusCode || "";
    const progressMap: Record<string, number> = {
      BACKLOG: 0,
      TODO: 10,
      IN_PROGRESS: 50,
      IN_REVIEW: 75,
      DONE: 100,
    };
    return progressMap[statusCode] || 0;
  };

  const transformedData = useMemo(() => {
    if (!bugsDataQuery?.data?.decryptedData?.items) return [];

    const getTaskColor = (type: string): string => {
      const colorMap: Record<string, string> = {
        EPIC: "#8B5CF6",
        STORY: "#3B82F6",
        DEFECT: "#EF4444",
        BUG: "#F59E0B",
        TASK: "#10B981",
      };
      return colorMap[type] || "#6B7280";
    };

    const transformTask = (task: any): TransformedTask => {
      const hasStartDate = !!task.startDate;
      const hasDueDate = !!task.dueDate;
      const hasValidDates = hasStartDate && hasDueDate;

      return {
        _id: task._id,
        title: task.summary,
        taskKey: task.bugKey,
        type: task.type,
        startDate: task.startDate || null,
        endDate: task.dueDate || null,
        hasValidDates,
        hasDueDate,
        hasStartDate,
        assignees:
          task.assignTo?.map((user: any) => ({
            _id: user._id,
            fullName: user.fullName || user.email?.split("@")[0] || "Unknown",
            email: user.email || "",
          })) || [],
        hours: task.estimation ? parseEstimation(task.estimation) : undefined,
        progress: calculateProgress(task),
        color: getTaskColor(task.type),
        taskCount: task.children?.length || task.subTasks?.length || 0,
        children: [
          ...(task.children?.map(transformTask) || []),
          ...(task.subTasks?.map(transformTask) || []),
        ],
      };
    };

    return bugsDataQuery?.data?.decryptedData.items.map(transformTask);
  }, [bugsDataQuery]);

  // Generate time periods based on view mode
  const timePeriods = useMemo(() => {
    const periods: { label: string; start: Date; end: Date; month?: string }[] =
      [];
    let current = new Date(startDate);

    if (viewMode === "today" || viewMode === "weeks") {
      let weekNum = 1;
      while (current <= endDate) {
        const weekStart = new Date(current);
        const weekEnd = new Date(current);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const month = weekStart.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });

        periods.push({
          label: `Week ${weekNum}`,
          start: weekStart,
          end: weekEnd > endDate ? endDate : weekEnd,
          month,
        });

        current.setDate(current.getDate() + 7);
        weekNum++;
      }
    } else if (viewMode === "months") {
      while (current <= endDate) {
        const monthStart = new Date(
          current.getFullYear(),
          current.getMonth(),
          1
        );
        const monthEnd = new Date(
          current.getFullYear(),
          current.getMonth() + 1,
          0
        );

        periods.push({
          label: monthStart.toLocaleDateString("en-US", { month: "short" }),
          start: monthStart,
          end: monthEnd > endDate ? endDate : monthEnd,
          month: monthStart.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          }),
        });

        current.setMonth(current.getMonth() + 1);
      }
    } else if (viewMode === "quarters") {
      while (current <= endDate) {
        const quarter = Math.floor(current.getMonth() / 3) + 1;
        const quarterStart = new Date(
          current.getFullYear(),
          (quarter - 1) * 3,
          1
        );
        const quarterEnd = new Date(current.getFullYear(), quarter * 3, 0);

        periods.push({
          label: `Q${quarter}`,
          start: quarterStart,
          end: quarterEnd > endDate ? endDate : quarterEnd,
          month: `${current.getFullYear()} Q${quarter}`,
        });

        current.setMonth(current.getMonth() + 3);
      }
    }

    return periods;
  }, [startDate, endDate, viewMode]);

  const handleHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollLeft = (
        e.target as HTMLDivElement
      ).scrollLeft;
    }
  };

  const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;

    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = target.scrollLeft;
    }

    if (taskNamesScrollRef.current) {
      taskNamesScrollRef.current.scrollTop = target.scrollTop;
    }
  };

  const handleTaskNamesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;

    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = target.scrollTop;
    }
  };

  const toggleExpand = (taskId: string) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const scrollToToday = () => {
    if (!contentScrollRef.current || !headerScrollRef.current) return;

    const today = new Date();
    const totalDays = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    const todayDay = Math.ceil(
      (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const scrollPosition = todayDay * 40;
    contentScrollRef.current.scrollLeft =
      scrollPosition - contentScrollRef.current.clientWidth / 2;
  };

  const flattenTasks = (
    tasks: TransformedTask[],
    level: number = 0
  ): Array<{ task: TransformedTask; level: number }> => {
    const result: Array<{ task: TransformedTask; level: number }> = [];

    tasks.forEach((task) => {
      result.push({ task, level });

      if (
        expandedTasks.has(task._id) &&
        task.children &&
        task.children.length > 0
      ) {
        result.push(...flattenTasks(task.children, level + 1));
      }
    });

    return result;
  };

  const flatTasks = useMemo(
    () => flattenTasks(transformedData),
    [transformedData, expandedTasks]
  );

  if (isBugsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          marginTop: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        maxHeight: "100vh",
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Top Toolbar */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #E5E7EB",
          bgcolor: "#FAFAFA",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MenuIcon sx={{ fontSize: 20, color: "#6B7280" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Timeline View
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            size="small"
            startIcon={<TodayIcon />}
            onClick={scrollToToday}
            sx={{
              textTransform: "none",
              color: theme.palette.primary.main,
              fontWeight: 500,
            }}
          >
            Jump to Today
          </Button>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                px: 2,
                py: 0.5,
                fontSize: 13,
                textTransform: "none",
                border: "1px solid #E5E7EB",
                "&.Mui-selected": {
                  bgcolor: theme.palette.primary.main,
                  color: "white",
                  "&:hover": {
                    bgcolor: theme.palette.primary.main,
                  },
                },
              },
            }}
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="weeks">Weeks</ToggleButton>
            <ToggleButton value="months">Months</ToggleButton>
            <ToggleButton value="quarters">Quarters</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Header Row */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        {/* Fixed Header for Task Names */}
        <Box
          sx={{
            width: 350,
            flexShrink: 0,
            bgcolor: "#FAFAFA",
            borderRight: "1px solid #E5E7EB",
            py: 1,
            px: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, fontSize: 13 }}
          >
            Tasks
          </Typography>
        </Box>

        {/* Scrollable Header for Timeline */}
        <Box
          ref={headerScrollRef}
          onScroll={handleHeaderScroll}
          sx={{
            flex: 1,
            overflowX: "auto",
            overflowY: "hidden",
            "&::-webkit-scrollbar": {
              height: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#D1D5DB",
              borderRadius: "4px",
            },
          }}
        >
          <TimelineHeader weeks={timePeriods} viewMode={viewMode} />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box
          ref={taskNamesScrollRef}
          onScroll={handleTaskNamesScroll}
          sx={{
            width: 350,
            flexShrink: 0,
            overflowY: "auto",
            overflowX: "hidden",
            borderRight: "1px solid #E5E7EB",
            maxHeight: "100%",
            "&::-webkit-scrollbar": {
              width: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#D1D5DB",
              borderRadius: "4px",
              "&:hover": {
                bgcolor: "#9CA3AF",
              },
            },
          }}
        >
          {flatTasks.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary" fontSize={13}>
                No tasks found
              </Typography>
            </Box>
          ) : (
            flatTasks.map(({ task, level }) => {
              const isExpanded = expandedTasks.has(task._id);
              const hasChildren = task.children && task.children.length > 0;

              return (
                <Box
                  key={task._id}
                  onClick={() => handleTaskClick(task._id)} // ✅ Handle click
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    paddingLeft: `${16 + level * 32}px`,
                    minHeight: 56,
                    borderBottom: "1px solid #E5E7EB",
                    bgcolor: "white",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                    },
                  }}
                >
                  {hasChildren ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(task._id);
                      }}
                      sx={{ width: 24, height: 24, p: 0 }}
                    >
                      {isExpanded ? (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  ) : (
                    <Box sx={{ width: 24, height: 24 }} />
                  )}

                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: task.color,
                      flexShrink: 0,
                    }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.25,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 11,
                          color: "#6B7280",
                          fontWeight: 500,
                        }}
                      >
                        {task.taskKey}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 10,
                          color: "#9CA3AF",
                          px: 0.75,
                          py: 0.25,
                          bgcolor: "#F3F4F6",
                          borderRadius: "4px",
                        }}
                      >
                        {task.type}
                      </Typography>
                      {!task.hasDueDate && !task.hasStartDate && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: 9,
                            color: "#F59E0B",
                            px: 0.5,
                            py: 0.25,
                            bgcolor: "#FEF3C7",
                            borderRadius: "4px",
                            fontWeight: 500,
                          }}
                        >
                          No dates
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        fontWeight: task.type === "EPIC" ? 600 : 400,
                        color: "#111827",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={task.title}
                    >
                      {task.title}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Scrollable Timeline Charts */}
        <Box
          ref={contentScrollRef}
          onScroll={handleContentScroll}
          sx={{
            flex: 1,
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "100%",
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#D1D5DB",
              borderRadius: "4px",
              "&:hover": {
                bgcolor: "#9CA3AF",
              },
            },
            "&::-webkit-scrollbar-corner": {
              bgcolor: "transparent",
            },
          }}
        >
          {flatTasks.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                Create your first task to see it in the timeline.
              </Typography>
            </Box>
          ) : (
            flatTasks.map(({ task, level }) => (
              <Box
                key={task._id}
                sx={{
                  minHeight: 56,
                  borderBottom: "1px solid #E5E7EB",
                }}
              >
                <TimelineChart
                  task={task}
                  weeks={timePeriods}
                  startDate={startDate}
                  endDate={endDate}
                  level={level}
                  viewMode={viewMode}
                  onTaskClick={handleTaskClick}
                />
              </Box>
            ))
          )}
        </Box>
      </Box>

      {selectedTaskId && (
        <TaskUpdateDrawer
          open={isDrawerOpen}
          onClose={handleDrawerClose}
          setBugId={() => {}}
          setDrawerOpen={setIsDrawerOpen}
          bugId={selectedTaskId || ""}
          users={users_data || []}
          createdBy={""}
          setCreatedBy={() => {}}
          refreshColumn={refetchBugs}
        />
      )}
    </Box>
  );
};

export default TimelineView;
